import type { ApiErrorBody, ApiErrorCode } from '@ssn/shared';

/**
 * A failed API call, carrying the server's own error envelope.
 *
 * Every message shown to a user comes from `message`: the server already writes
 * them for humans, so the client never invents its own wording and the two can
 * never disagree.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: Readonly<Record<string, readonly string[]>> | undefined;
  readonly requestId: string | undefined;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    details?: Readonly<Record<string, readonly string[]>>,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** The first message for a form field, if the server rejected that field. */
  fieldError(path: string): string | undefined {
    return this.details?.[path]?.[0];
  }

  get isAuthProblem(): boolean {
    return this.code === 'UNAUTHENTICATED';
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Sent as multipart instead of JSON. */
  readonly formData?: FormData;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;

  if (options.formData) {
    // Let the browser set the multipart boundary; setting Content-Type by hand
    // omits it and the server cannot parse the body.
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const response = await fetch(path, {
    method,
    headers,
    ...(body !== undefined ? { body } : {}),
    // The session lives in an HttpOnly cookie, so it must be sent along.
    credentials: 'same-origin',
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const envelope = payload as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? 'INTERNAL_ERROR',
      envelope?.error?.message ?? `The request failed (${response.status}).`,
      envelope?.error?.details,
      envelope?.error?.requestId,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: 'DELETE', body }),
  upload: <T>(path: string, formData: FormData): Promise<T> =>
    request<T>(path, { method: 'POST', formData }),
};

/**
 * Builds a query string from a filter object, dropping empty values so a URL
 * only ever carries filters that are actually applied.
 */
export function toQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(','));
    } else if (typeof value === 'boolean') {
      if (value) search.set(key, 'true');
    } else {
      search.set(key, String(value));
    }
  }

  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}
